import type { JobResponse } from '@siteday/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { errorMessage } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Select } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';

import { useJobsList, useReconcileJob } from './useJobs';

/** Merge a field-created job into a real one: entries move, the source is soft-deleted (doc 05). */
export function ReconcileDialog({
  job,
  open,
  onClose,
}: {
  job: JobResponse;
  open: boolean;
  onClose: () => void;
}) {
  const jobs = useJobsList({ status: 'active' });
  const [target, setTarget] = useState('');
  const reconcile = useReconcileJob();
  const toast = useToast();
  const navigate = useNavigate();
  const targets = (jobs.data?.items ?? []).filter((j) => j.id !== job.id && !j.createdByForeman);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Reconcile "${job.name}"`}
      description="Moves every entry and cost code from this field-created job into the job you pick, then removes it. Nothing the foreman logged is lost."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!target}
            loading={reconcile.isPending}
            onClick={() =>
              reconcile.mutate(
                { id: job.id, intoJobId: target },
                {
                  onSuccess: (merged) => {
                    toast.success(`Merged into ${merged.name}`);
                    onClose();
                    navigate(`/jobs/${merged.id}`);
                  },
                  onError: (e) => toast.error(errorMessage(e)),
                },
              )
            }
          >
            Merge entries
          </Button>
        </>
      }
    >
      <Field label="Merge into" htmlFor="reconcile-target">
        <Select id="reconcile-target" value={target} onChange={(e) => setTarget(e.target.value)}>
          <option value="">Pick a job…</option>
          {targets.map((j) => (
            <option key={j.id} value={j.id}>
              {j.name}
              {j.clientName ? ` · ${j.clientName}` : ''}
            </option>
          ))}
        </Select>
      </Field>
    </Dialog>
  );
}
